import { useEffect, useRef, useState } from 'react'
import jsLogger from 'js-logger'

/**
 * Microphone health, used to surface a "we can't hear you" indication.
 *
 * - `ok`     — the audio track is live and delivering (or we are not monitoring).
 * - `muted`  — the audio MediaStreamTrack reports `muted === true` (or the track
 *              ended). This is a read-only, user-agent/OS-level signal: hardware
 *              mute, OS mixer muted, the device unplugged, or another app holding
 *              it. This is exactly what was observed in the field log — the track
 *              delivered pure silence to Deepgram and every transcript was empty.
 * - `silent` — kept for the forced-testing path (`?micWarn=silent`) and warning
 *              copy. We intentionally do NOT detect this from audio energy:
 *              a working mic in a quiet room (with noise suppression) produces
 *              flat-zero input that is indistinguishable from a dead device, so
 *              energy-based detection false-fires whenever the user is simply
 *              not speaking. `track.muted` is the reliable signal.
 */
export type MicHealthStatus = 'ok' | 'muted' | 'silent'

export type MicHealth = {
  status: MicHealthStatus
  /** true once the problem has persisted long enough to escalate (e.g. banner). */
  escalated: boolean
}

type Options = {
  /** Only monitor while true (e.g. mic control enabled and session active). */
  active?: boolean
  sampleIntervalMs?: number
  /** Continuous `muted` duration before flagging `muted` (debounce transients). */
  mutedDebounceMs?: number
  /** Duration in a non-ok status before `escalated` becomes true. */
  escalateMs?: number
}

const DEFAULTS = {
  sampleIntervalMs: 250,
  mutedDebounceMs: 1_000,
  escalateMs: 4_000
}

const toNum = (v: string | null | undefined): number | undefined => {
  if (v == null || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

// Build-time defaults (CRA inlines static `process.env.REACT_APP_*` reads).
const ENV = {
  sampleIntervalMs: toNum(process.env.REACT_APP_MIC_WARN_SAMPLE_MS),
  mutedDebounceMs: toNum(process.env.REACT_APP_MIC_WARN_MUTED_MS),
  escalateMs: toNum(process.env.REACT_APP_MIC_WARN_ESCALATE_MS)
}

/**
 * Read runtime overrides from the URL query string. Lets you tune/force the
 * warning on a deployed build without code changes, e.g.:
 *   ?micWarn=muted           force the status (ok | muted | silent), bypasses
 *                            the mic checks — handy for eyeballing the UI
 *   ?micWarnEscalateMs=1000  show the banner sooner
 *   ?micWarnMutedMs=300 &micWarnSampleMs=100
 */
const readUrlOverrides = () => {
  let params: URLSearchParams | null = null
  try {
    params = new URLSearchParams(window.location.search)
  } catch {
    params = null
  }
  const forcedRaw = params?.get('micWarn')
  const forced: MicHealthStatus | null =
    forcedRaw === 'ok' || forcedRaw === 'muted' || forcedRaw === 'silent'
      ? forcedRaw
      : null
  return {
    forced,
    sampleIntervalMs: toNum(params?.get('micWarnSampleMs')),
    mutedDebounceMs: toNum(params?.get('micWarnMutedMs')),
    escalateMs: toNum(params?.get('micWarnEscalateMs'))
  }
}

const useMicHealth = (
  stream: MediaStream | null,
  options: Options = {}
): MicHealth => {
  // Read URL overrides once on mount. Precedence: explicit option > URL > env > default.
  const overridesRef = useRef(readUrlOverrides())
  const ov = overridesRef.current

  const { active = true } = options
  const sampleIntervalMs =
    options.sampleIntervalMs ??
    ov.sampleIntervalMs ??
    ENV.sampleIntervalMs ??
    DEFAULTS.sampleIntervalMs
  const mutedDebounceMs =
    options.mutedDebounceMs ??
    ov.mutedDebounceMs ??
    ENV.mutedDebounceMs ??
    DEFAULTS.mutedDebounceMs
  const escalateMs =
    options.escalateMs ?? ov.escalateMs ?? ENV.escalateMs ?? DEFAULTS.escalateMs
  const forcedStatus = ov.forced

  const [health, setHealth] = useState<MicHealth>({
    status: 'ok',
    escalated: false
  })

  // Refs so the sampling loop reads the latest values without re-subscribing.
  const mutedSinceRef = useRef<number>(0)
  const statusSinceRef = useRef<number>(0)
  const currentStatusRef = useRef<MicHealthStatus>('ok')

  useEffect(() => {
    // Forced status from URL (?micWarn=...) — bypass all checks, for UI testing.
    if (forcedStatus) {
      setHealth({
        status: forcedStatus,
        escalated: forcedStatus !== 'ok'
      })
      return
    }

    if (!active || !stream) {
      // Reset to healthy when not monitoring (e.g. mic intentionally off).
      currentStatusRef.current = 'ok'
      statusSinceRef.current = 0
      setHealth({ status: 'ok', escalated: false })
      return
    }

    const audioTrack = stream.getAudioTracks()[0]
    if (!audioTrack) {
      setHealth({ status: 'ok', escalated: false })
      return
    }

    const now = Date.now()
    mutedSinceRef.current = audioTrack.muted ? now : 0

    const onMute = () => {
      mutedSinceRef.current = Date.now()
    }
    const onUnmute = () => {
      mutedSinceRef.current = 0
    }
    audioTrack.addEventListener('mute', onMute)
    audioTrack.addEventListener('unmute', onUnmute)

    const applyStatus = (next: MicHealthStatus) => {
      const ts = Date.now()
      if (next !== currentStatusRef.current) {
        currentStatusRef.current = next
        statusSinceRef.current = next === 'ok' ? 0 : ts
        jsLogger.warn('mic-health: status changed', {
          status: next,
          muted: audioTrack.muted,
          trackState: audioTrack.readyState
        })
      }
      const escalated =
        next !== 'ok' &&
        statusSinceRef.current > 0 &&
        ts - statusSinceRef.current >= escalateMs
      setHealth((prev) =>
        prev.status === next && prev.escalated === escalated
          ? prev
          : { status: next, escalated }
      )
    }

    const tick = () => {
      const ts = Date.now()
      const muted = audioTrack.muted || audioTrack.readyState === 'ended'

      if (muted) {
        if (!mutedSinceRef.current) mutedSinceRef.current = ts
        if (ts - mutedSinceRef.current >= mutedDebounceMs) {
          applyStatus('muted')
        } else {
          applyStatus('ok')
        }
      } else {
        mutedSinceRef.current = 0
        applyStatus('ok')
      }
    }

    const timer = window.setInterval(tick, sampleIntervalMs)

    return () => {
      window.clearInterval(timer)
      audioTrack.removeEventListener('mute', onMute)
      audioTrack.removeEventListener('unmute', onUnmute)
    }
  }, [stream, active, forcedStatus, sampleIntervalMs, mutedDebounceMs, escalateMs])

  return health
}

export default useMicHealth
